import { all, call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { ValidEmail } from '@dev/ee-api/ee-api-models';

import i18n from '@/../i18n';
import { addValidEmail, deleteValidEmail, loadValidEmails, sendVerification } from '@/services/ElasticApi';
import {
    AddValidEmailError,
    AddValidEmailsSuccess,
    DeleteValidEmailError,
    DeleteValidEmailSuccess,
    LoadValidEmailsError,
    LoadValidEmailsSuccess,
} from '@/store/validemails/actions';
import {
    AddValidEmailAction,
    DeleteValidEmailAction,
    ResendEmailVerificationAction,
    ValidEmailsActionTypes,
} from '@/store/validemails/types';
import { ShowToast } from '@/store/toast/actions';
import { ApplicationState as State } from '@/store';
import { AccountState } from '@/store/account/types';
import { SettingsPaths } from '@/services/dictionaries/Paths';
import { ConfigState } from '@/store/config/types';
import { Logger } from '@/services/errors/appLogger';

const getValidEmails = (state: State): ValidEmail[] => state.validEmails.emails;
const getAccount = (state: State): AccountState => state.account;
const getConfig = (state: State): ConfigState => state.config;

function* fetchValidEmails(): any {
    try {
        const validEmails = yield call(loadValidEmails);

        yield put(LoadValidEmailsSuccess(validEmails.data));
    } catch (e) {
        console.log(e);
        yield put(LoadValidEmailsError());
        yield put(
            ShowToast({
                message: `Error: ${e?.response?.data?.Error} `,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}
function* removeValidEmail(action: DeleteValidEmailAction): any {
    const {
        payload: { email },
    } = action;
    try {
        yield all(email.map((singleEmail): any => call(deleteValidEmail, singleEmail)));
        const userValidEmails: ValidEmail[] = yield select(getValidEmails);
        const updatedValidEmails = userValidEmails?.filter((singleEmail): boolean => {
            // eslint-disable-next-line sonarjs/prefer-single-boolean-return
            if (email.includes(singleEmail.Email)) {
                return false;
            }
            return true;
        });
        yield put(DeleteValidEmailSuccess(updatedValidEmails));
        yield put(
            ShowToast({
                message: i18n.t('emaildeleted', { email }),
                severity: 'info',
                duration: 3000,
            })
        );
    } catch (e) {
        console.log(e);
        yield put(DeleteValidEmailError());
        yield put(
            ShowToast({
                message: `Error: ${e?.response?.data?.Error} `,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}
function* sendEmailVerification(action: ResendEmailVerificationAction): any {
    const {
        payload: { email },
    } = action;
    const {
        GeneralInfo: { PrivateBrandingUrl },
        AdvancedOptions,
    }: AccountState = yield select(getAccount) || {};

    const { bootedAsChild, isStandalone } = yield select(getConfig);
    const { IsOwnedByReseller } = AdvancedOptions || {};

    const locationPathname = isStandalone ? '' : `${location.pathname}#`;
    const marketingUrl = `${(IsOwnedByReseller && PrivateBrandingUrl) || location.origin}${
        bootedAsChild ? '/marketing' : locationPathname
    }`;

    const ReturnUrl = `${marketingUrl}${SettingsPaths.Domains}`;

    try {
        yield call(sendVerification, { ValidEmail: email, ReturnUrl });

        yield put(
            ShowToast({
                message: i18n.t('emailsent'),
                severity: 'info',
                duration: 3000,
            })
        );
    } catch (e) {
        console.log(e);
        yield put(
            ShowToast({
                message: `Error: ${e?.response?.data?.Error} `,
                severity: 'error',
                duration: 3000,
            })
        );
    }
}
function* addingValidEmail(action: AddValidEmailAction): any {
    const {
        payload: { email },
        meta,
    } = action;
    const {
        GeneralInfo: { PrivateBrandingUrl },
        AdvancedOptions,
    }: AccountState = yield select(getAccount) || {};

    const { bootedAsChild, isStandalone } = yield select(getConfig);
    const { IsOwnedByReseller } = AdvancedOptions || {};

    const locationPathname = isStandalone ? '' : `${location.pathname}#`;
    const marketingUrl = `${(IsOwnedByReseller && PrivateBrandingUrl) || location.origin}${
        bootedAsChild ? '/marketing' : locationPathname
    }`;

    const ReturnUrl = `${marketingUrl}${SettingsPaths.DomainsEmailVerification}`;

    try {
        const addedEmail = yield call(addValidEmail, { ValidEmail: email, ReturnUrl });
        yield put(AddValidEmailsSuccess(addedEmail.data, meta));

        yield put(
            ShowToast({
                message: i18n.t('emailsent'),
                severity: 'info',
                duration: 3000,
            })
        );
    } catch (e) {
        const message = `${e?.response?.data?.Error || e?.message}`;
        yield put(
            AddValidEmailError(
                { message, messageId: e?.response?.data?.MessageID, messageParts: e?.response?.data?.MessageParts },
                meta
            )
        );
        Logger.error(`Error: ${message}`);
    }
}

function* watchFetchValidEmails(): any {
    yield takeEvery(ValidEmailsActionTypes.VALIDEMAILS_FETCH_START, fetchValidEmails);
}
function* watchForDeleteValidEmail(): any {
    yield takeEvery(ValidEmailsActionTypes.VALIDEMAILS_DELETE, removeValidEmail);
}
function* wachForResendingVerificationEmails(): any {
    yield takeEvery(ValidEmailsActionTypes.VALIDEMAILS_RESEND_VERIFICATION, sendEmailVerification);
}
function* watchForAddEmails(): any {
    yield takeEvery(ValidEmailsActionTypes.VALIDEMAILS_ADD, addingValidEmail);
}

const ValidEmailsSagas = [
    fork(watchFetchValidEmails),
    fork(watchForDeleteValidEmail),
    fork(wachForResendingVerificationEmails),
    fork(watchForAddEmails),
];

export default ValidEmailsSagas;
